import { ButtonV2 } from "#src/commonV2/Button";
import paymentService from "#src/services/payment";
import { CheckCircle, Download02 } from "@untitled-ui/icons-react";
import { ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import PaymentConfirmModal from "./ConfirmModal";

interface IPaymentProcessPaymentProps {
  workspaceId: string;
  subscriptionBanking: Awaited<
    ReturnType<typeof paymentService.createSubscriptionBanking>
  >;
  settingPath: string;
}

export const PaymentProcessPayment = ({
  workspaceId,
  subscriptionBanking,
  settingPath,
}: IPaymentProcessPaymentProps) => {
  const { t } = useTranslation("common", {
    keyPrefix: "v2.payment.processPayment",
  });

  // --------------- STATES ---------------

  const [openPaymentConfirm, setOpenPaymentConfirm] = useState<boolean>(false);

  // --------------- METHODS ---------------

  const BankingAccountInfoItem = ({
    label,
    value,
  }: {
    label: string;
    value: ReactNode;
  }) => {
    return (
      <div className="grid grid-cols-subgrid col-span-full items-center">
        <span className="text-md text-gray-v2-600">{label}</span>
        <span className="text-md font-semibold text-gray-v2-900 flex items-center gap-3">
          {value}
        </span>
      </div>
    );
  };

  // --------------- RENDER ---------------

  return (
    <>
      <div className="flex flex-col gap-5">
        {/* Title block */}
        <div className="w-full py-3 flex items-center justify-center text-lg font-semibold text-gray-v2-900">
          {t("title")}
        </div>
        {/* Title block end */}

        <div className="p-6 grid grid-cols-2 gap-6 rounded-xl border-2 border-brand-v2-300">
          {/* Left half (QR image) */}
          <div className="p-6 flex flex-col items-center gap-5 rounded-xl shadow-xs border border-gray-v2-200 bg-gray-v2-50">
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-lg text-gray-v2-600">
                {t("qrCode.scanThisToMakePayment")}
              </span>
              <span className="text-xl font-semibold text-gray-v2-900">
                CYSTACK JSC - 888310000
              </span>
            </div>
            <img src={subscriptionBanking.bank_qr_url} className="h-80 w-80" />
            <span className="font-bold-28 text-primary">
              {subscriptionBanking.total_price.toLocaleString()}{" "}
              {subscriptionBanking.currency}
            </span>
            <ButtonV2 variant="secondary" className="h-11">
              <Download02 className="h-5 w-5 min-w-5" />
            </ButtonV2>
          </div>
          {/* Left half (QR image) end */}

          {/* Right half (info & note) */}
          <div className="flex flex-col justify-between rounded-xl shadow-xs border border-gray-v2-200">
            <div className="p-6 grid grid-cols-[4fr_5fr] gap-6">
              <BankingAccountInfoItem
                label={t("accountInfo.accountNumber")}
                value={"888310000"}
              />
              <BankingAccountInfoItem
                label={t("accountInfo.accountName")}
                value={"CYSTACK JSC"}
              />
              <BankingAccountInfoItem
                label={t("accountInfo.bank")}
                value={
                  <>
                    <span>MB Bank</span>
                    <span className="font-normal text-gray-v2-600">
                      Ngân hàng Quân Đội
                    </span>
                  </>
                }
              />
              <BankingAccountInfoItem
                label={t("accountInfo.total")}
                value={
                  <span className="text-xl font-semibold text-brand-v2-700">
                    {subscriptionBanking.total_price.toLocaleString()}{" "}
                    {subscriptionBanking.currency}
                  </span>
                }
              />
              <BankingAccountInfoItem
                label={t("accountInfo.transferMessage")}
                value={subscriptionBanking.payment_id}
              />
            </div>
            <div className="p-6 flex flex-col gap-6 bg-gray-v2-100 text-gray-v2-600 text-md">
              <div className="flex flex-col gap-3">
                <b>{t("note.note")}</b>
                <Trans
                  t={t}
                  i18nKey={"note.transferNoteContent"}
                  components={{ b: <b className="contents" /> }}
                  values={{ id: subscriptionBanking.payment_id }}
                />
                <br />
                <Trans
                  t={t}
                  i18nKey={"note.afterTransfer"}
                  components={{ b: <b className="contents" /> }}
                />
              </div>
              <ButtonV2
                onClick={() => {
                  setOpenPaymentConfirm(true);
                }}
              >
                {t("note.button.confirmTransfer")}
                <CheckCircle className="h-5 w-5 min-w-5" />
              </ButtonV2>
            </div>
          </div>
          {/* Right half (info & note) end */}
        </div>
      </div>
      {/* TODO: this modal is common for both banking and card payment. */}
      <PaymentConfirmModal
        workspaceId={workspaceId}
        open={openPaymentConfirm}
        onChangeOpen={(value) => {
          setOpenPaymentConfirm(value);
        }}
        paymentInfo={subscriptionBanking}
        settingPath={settingPath}
      />
    </>
  );
};
